import React, { useState, useCallback } from 'react';
import {
  Button,
  IconButton,
  Stack,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';

// Иконки
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PageviewIcon from '@mui/icons-material/Pageview';

function EditorToolbar({ 
  onInsertTable, 
  onInsertVariable, 
  onExportPDF, 
  onExportWord, 
  onSave, 
  onPrint, 
  onPageMargins, // Новый пропс
  editorRef, 
  forceUpdateContent, 
  readOnly = false,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false
}) {
  const [fontSize, setFontSize] = useState('14');
  const [lineHeight, setLineHeight] = useState('1.6');
  
  const formatText = useCallback((command) => {
    if (!editorRef?.current) return;
    
    try {
      editorRef.current.focus();
      document.execCommand(command, false, null);
      if (forceUpdateContent) forceUpdateContent();
    } catch (error) {
      console.error(`Ошибка форматирования ${command}:`, error);
    }
  }, [editorRef, forceUpdateContent]);

  const insertHeading = useCallback((level) => {
    if (!editorRef?.current) return;
    
    try {
      editorRef.current.focus();
      document.execCommand('formatBlock', false, `h${level}`);
      if (forceUpdateContent) forceUpdateContent();
    } catch (error) {
      console.error(`Ошибка заголовка H${level}:`, error);
    }
  }, [editorRef, forceUpdateContent]);

  const insertList = useCallback((type) => {
    if (!editorRef?.current) return;
    
    try {
      editorRef.current.focus();
      const command = type === 'bullet' ? 'insertUnorderedList' : 'insertOrderedList';
      document.execCommand(command, false, null);
      if (forceUpdateContent) forceUpdateContent();
    } catch (error) {
      console.error(`Ошибка списка ${type}:`, error);
    }
  }, [editorRef, forceUpdateContent]);

  const alignText = useCallback((alignment) => {
    if (!editorRef?.current) return;
    
    try {
      editorRef.current.focus();
      document.execCommand('justify' + alignment, false, null);
      if (forceUpdateContent) forceUpdateContent();
    } catch (error) {
      console.error(`Ошибка выравнивания ${alignment}:`, error);
    }
  }, [editorRef, forceUpdateContent]);

  // ПРОСТАЯ И НАДЕЖНАЯ функция изменения размера шрифта
  const changeFontSize = useCallback((size) => {
    if (!editorRef?.current) return;
    
    setFontSize(size);
    
    try {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !editorRef.current.contains(selection.getRangeAt(0).commonAncestorContainer)) return;
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        
        // Проверяем, находимся ли мы в таблице
        const commonAncestor = range.commonAncestorContainer;
        const table = commonAncestor.nodeType === Node.TEXT_NODE 
          ? commonAncestor.parentElement?.closest('table')
          : commonAncestor.closest?.('table');
        
        if (table) {
          // ДЛЯ ТАБЛИЦ - применяем стиль к ячейкам напрямую
          const tableCells = table.querySelectorAll('td, th');
          const affectedCells = [];
          
          // Находим ячейки которые пересекаются с выделением
          tableCells.forEach(cell => {
            try {
              if (range.intersectsNode(cell)) {
                affectedCells.push(cell);
              }
            } catch (e) {
              // Проверяем простым способом
              const cellText = cell.textContent;
              const selectedText = range.toString();
              if (selectedText.includes(cellText) || cellText.includes(selectedText)) {
                affectedCells.push(cell);
              }
            }
          });
          
          console.log('🎯 Применяем размер шрифта', size + 'px', 'к', affectedCells.length, 'ячейкам');
          
          // Применяем размер шрифта к ячейкам
          affectedCells.forEach(cell => {
            cell.style.fontSize = size + 'px';
            
            // Убираем все вложенные span с размером шрифта чтобы не накапливались
            const existingSpans = cell.querySelectorAll('span[style*="font-size"]');
            existingSpans.forEach(span => {
              if (span.style.fontSize) {
                const parent = span.parentNode;
                while (span.firstChild) {
                  parent.insertBefore(span.firstChild, span);
                }
                parent.removeChild(span);
              }
            });
          });
          
        } else {
          // ДЛЯ ОБЫЧНОГО ТЕКСТА - используем простой span
          const selectedText = range.toString();
          const span = document.createElement('span');
          span.style.fontSize = size + 'px';
          span.textContent = selectedText;
          
          range.deleteContents();
          range.insertNode(span);
          
          // Устанавливаем курсор после span
          range.setStartAfter(span);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        // Если нет выделения, применяем к следующему вводимому тексту
        const span = document.createElement('span');
        span.style.fontSize = size + 'px';
        span.innerHTML = '&nbsp;'; // Неразрывный пробел
        
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.insertNode(span);
          range.setStartAfter(span);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      
      if (forceUpdateContent) forceUpdateContent();
      
    } catch (error) {
      console.error('Ошибка изменения размера шрифта:', error);
    }
  }, [editorRef, forceUpdateContent]);

  const changeLineHeight = useCallback((height) => {
    if (!editorRef?.current) return;
    
    setLineHeight(height);
    
    try {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !editorRef.current.contains(selection.getRangeAt(0).commonAncestorContainer)) return;
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        const div = document.createElement('div');
        div.style.lineHeight = height;
        
        try {
          range.surroundContents(div);
        } catch (e) {
          div.appendChild(range.extractContents());
          range.insertNode(div);
        }
      } else {
        if (editorRef.current) {
          editorRef.current.style.lineHeight = height;
        }
      }
      
      if (forceUpdateContent) forceUpdateContent();
    } catch (error) {
      console.error('Ошибка межстрочного расстояния:', error);
    }
  }, [editorRef, forceUpdateContent]);

  return (
    <Paper sx={{ p: 1 }} className="toolbar">
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        {!readOnly && (
          <>
            <IconButton onClick={onUndo} disabled={!canUndo} size="small" title="Отменить">
              <UndoIcon />
            </IconButton>
            <IconButton onClick={onRedo} disabled={!canRedo} size="small" title="Повторить">
              <RedoIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem />
            
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Размер</InputLabel>
              <Select value={fontSize} label="Размер" onChange={(e) => changeFontSize(e.target.value)}>
                <MenuItem value="8">8px</MenuItem>
                <MenuItem value="10">10px</MenuItem>
                <MenuItem value="12">12px</MenuItem>
                <MenuItem value="14">14px</MenuItem>
                <MenuItem value="16">16px</MenuItem>
                <MenuItem value="18">18px</MenuItem>
                <MenuItem value="20">20px</MenuItem>
                <MenuItem value="24">24px</MenuItem>
                <MenuItem value="28">28px</MenuItem>
                <MenuItem value="32">32px</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Интервал</InputLabel>
              <Select value={lineHeight} label="Интервал" onChange={(e) => changeLineHeight(e.target.value)}>
                <MenuItem value="1">1.0</MenuItem>
                <MenuItem value="1.15">1.15</MenuItem>
                <MenuItem value="1.5">1.5</MenuItem>
                <MenuItem value="1.6">1.6</MenuItem>
                <MenuItem value="2">2.0</MenuItem>
                <MenuItem value="2.5">2.5</MenuItem>
                <MenuItem value="3">3.0</MenuItem>
              </Select>
            </FormControl>
            
            <Divider orientation="vertical" flexItem />
            
            <IconButton onClick={() => formatText('bold')} size="small" title="Жирный">
              <FormatBoldIcon />
            </IconButton>
            <IconButton onClick={() => formatText('italic')} size="small" title="Курсив">
              <FormatItalicIcon />
            </IconButton>
            <IconButton onClick={() => formatText('underline')} size="small" title="Подчеркнутый">
              <FormatUnderlinedIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem />
            
            <IconButton onClick={() => alignText('Left')} size="small" title="Слева">
              <FormatAlignLeftIcon />
            </IconButton>
            <IconButton onClick={() => alignText('Center')} size="small" title="По центру">
              <FormatAlignCenterIcon />
            </IconButton>
            <IconButton onClick={() => alignText('Right')} size="small" title="Справа">
              <FormatAlignRightIcon />
            </IconButton>
            <IconButton onClick={() => alignText('Full')} size="small" title="По ширине">
              <FormatAlignJustifyIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem />
            
            <Button size="small" onClick={() => insertHeading(1)} variant="outlined">H1</Button>
            <Button size="small" onClick={() => insertHeading(2)} variant="outlined">H2</Button>
            <Button size="small" onClick={() => insertHeading(3)} variant="outlined">H3</Button>
            
            <Divider orientation="vertical" flexItem />
            
            <IconButton onClick={() => insertList('bullet')} size="small" title="Список">
              <FormatListBulletedIcon />
            </IconButton>
            <IconButton onClick={() => insertList('numbered')} size="small" title="Нумерованный список">
              <FormatListNumberedIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem />
            
            <Button variant="contained" startIcon={<TableChartIcon />} onClick={onInsertTable} size="small" color="success">
              Таблица
            </Button>
            
            {onInsertVariable && (
              <Button variant="outlined" startIcon={<CodeIcon />} onClick={onInsertVariable} size="small">
                Переменные
              </Button>
            )}
            
            {onPageMargins && (
              <Button variant="outlined" startIcon={<PageviewIcon />} onClick={onPageMargins} size="small" color="info">
                Отступы
              </Button>
            )}
            
            <Divider orientation="vertical" flexItem />
            
            <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} size="small" color="primary">
              Сохранить
            </Button>
            
            <Divider orientation="vertical" flexItem />
          </>
        )}
        
        <Button variant="contained" startIcon={<PrintIcon />} onClick={onPrint} size="small" color="secondary">
          Предпросмотр
        </Button>
        
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportPDF} size="small">
          PDF
        </Button>
        
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportWord} size="small">
          DOCX
        </Button>
      </Stack>
    </Paper>
  );
}

export default EditorToolbar;
